import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Customers, Orders } from '../../interfaces';
import { DataServiceService } from '../../services/data-service.service';

@Component({
  selector: 'app-week-calendar',
  templateUrl: './week-calendar.component.html',
  styleUrl: './week-calendar.component.scss'
})
export class WeekCalendarComponent implements OnChanges {
  constructor(private dataServiceService: DataServiceService) {}
  
  @Input() customers: Customers[] = [];
  @Input() orders: Orders[] = [];
  
  weekDays: Date[] = [];
  today = new Date();
  filteredOrders: Orders[] = []; // Display filtered orders

  ngOnInit() {
    this.loadNextSevenDays(this.today);
  }

  ngOnChanges(changes: SimpleChanges) {
    // If orders are updated, refilter the orders for the current week
    if (changes['orders']) {
      console.log('Orders updated in week view, refiltering');
      this.filterOrdersForCurrentWeek();
    }
  }

  getCustomerName(customerId: number): string {
    const customer = this.customers.find(c => c.customer_id === customerId);
    return customer ? `${customer.name.charAt(0).toUpperCase() + customer.name.slice(1).toLowerCase()}` : 'Unknown';
  }

  previousDay() {
    // Shift the entire date range back by 1 day
    this.weekDays = this.weekDays.map(date => {
      const newDate = new Date(date);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
    
    // Filter orders for the new date range
    this.filterOrdersForCurrentWeek();
  }

  nextDay() {
    // Shift the entire date range forward by 1 day
    this.weekDays = this.weekDays.map(date => {
      const newDate = new Date(date);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
    
    // Filter orders for the new date range
    this.filterOrdersForCurrentWeek();
  }

  private generateDateRange(startDate: Date, endDate: Date) {
    this.weekDays = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      this.weekDays.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
  }

  loadNextSevenDays(date: Date) {
    const startDate = new Date(date);
    const endDate = new Date(date);
    endDate.setDate(date.getDate() + 6);
    
    // Generate the 7-day array
    this.generateDateRange(startDate, endDate);
    
    // Filter orders for this week
    this.filterOrdersForCurrentWeek();
  }


  private filterOrdersForCurrentWeek() {
    if (this.weekDays.length === 0) return;
    
    const startDate = new Date(this.weekDays[0]);
    const endDate = new Date(this.weekDays[this.weekDays.length - 1]);
    
    // Set time boundaries for accurate filtering
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    // Filter from ALL orders, not the already filtered ones
    this.filteredOrders = this.orders.filter(order => {
      const orderDate = new Date(order.pickup_date || order.delivery_date);
      return orderDate >= startDate && orderDate <= endDate;
    });
  }

  // Helper method to get orders for a specific day
  getOrdersForDate(date: Date): Orders[] {
    return this.filteredOrders.filter(order => {
      const orderDate = new Date(order.delivery_date || order.pickup_date);
      return this.isSameDate(date, orderDate);
    });
  }

  isSameDate(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return this.isSameDate(date, today);
  }
}
